/**
 * Impersonazione: sessione di 30 minuti, senza refresh; sul portale in sola lettura.
 * Il refresh token salvato resta del superadmin: stop, scadenza o reload lo riportano a sé.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { httpRequest } from '@/shared/api/client';
import { useAuth } from '@/app/providers/AuthProvider';
import { isPortalSessionSync } from '@/shared/lib/auth-state';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import { selectAccess } from '@/features/portal/portalAccess';
import type { AuthEnvelope } from './types';

/** Come è finita una sessione (`ImpersonationLog.endReason`). */
export const IMPERSONATION_END_REASON: Record<string, string> = {
  stopped: 'terminata',
  returned: 'tornato a sé',
  superseded: 'sostituita',
  expired: 'scaduta',
};

export function impersonatedBy(decoded: unknown): string | null {
  const v = (decoded as Record<string, unknown> | null)?.impersonated_by;
  return typeof v === 'string' && v !== '' ? v : null;
}

export interface ImpersonationTarget {
  email: string;
  /** L'accesso di portale da aprire; finisce nel log. */
  portalAccountId?: string | null;
}

export function useStartImpersonation() {
  const { loginWithEnvelope } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (target: ImpersonationTarget) =>
      httpRequest<AuthEnvelope>('/api/account/impersonate', {
        method: 'POST',
        endpointType: 'plain',
        body: { TargetUserEmail: target.email, TargetPortalAccountId: target.portalAccountId ?? null },
      }),
    onSuccess: (envelope, target) => {
      // Cache e committente scelto sono del superadmin.
      qc.clear();
      // Il server accetta solo l'accesso scritto nel log.
      selectAccess(target.portalAccountId ?? null);
      loginWithEnvelope(envelope);
      void navigate({ to: isPortalSessionSync() ? '/portal' : '/' });
    },
    onError: (err) => notifyError(err, 'Impersonificazione non avviata'),
  });
}

export function useStopImpersonation() {
  const { refresh } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async () => {
      // Chiude il log; il token torna col refresh.
      await httpRequest('/api/account/stop-impersonating', { method: 'POST' });
      await refresh();
    },
    onSuccess: () => {
      qc.clear();
      selectAccess(null);
      notifySuccess('Impersonificazione terminata: sei di nuovo tu.');
      void navigate({ to: '/' });
    },
    onError: (err) => notifyError(err, 'Impersonificazione non terminata — riprova'),
  });
}
